/**
 * Cliente REST da musa-generator-api para o que não é CRUD de usuário (esse mora em
 * `keycloak/client.ts`). Mesma base `VITE_GENERATOR_API_URL` (já termina em `/v1`) e o mesmo
 * Bearer do usuário; o escopo vem do claim `generators[]` do token (realm `musa` vê tudo).
 */

import { getValidToken } from '@/integrations/keycloak/client';

const API_URL = import.meta.env.VITE_GENERATOR_API_URL;

/** Gerador raiz (N1) visível ao usuário. */
export interface GeneratorOption {
  id: string;
  name: string;
  external_id: string;
}

/** Nó da árvore de geradores visível ao usuário (raiz incluída). */
export interface EstablishmentOption {
  id: string;
  name: string;
  external_id: string;
  /** Id do gerador pai; nulo na raiz. */
  parent_id: number | null;
  /** Profundidade na árvore: raiz = 1. */
  n_level?: number;
  /** Id do gerador N1 raiz da hierarquia. */
  generator_id?: string | null;
  /** UF do endereço de coleta (`pickup`); nulo sem endereço. Traduz filtro de UF em unidades. */
  state?: string | null;
  city?: string | null;
}

/** UF com endereço de alguma unidade visível. */
export interface StateOption {
  name: string;
  value: string;
}

/** Material do filtro global — vem do catálogo do musa-agreements, mas mora no mesmo store. */
export interface MaterialOption {
  id: string;
  name: string;
}

export interface FetchFilterOptionsResult {
  generators: GeneratorOption[];
  establishments: EstablishmentOption[];
  states: StateOption[];
}

export interface UnidadesGenerator {
  id: string;
  name: string;
}

export interface UnidadesEstablishment {
  id: string;
  name: string;
  generator_id: string | null;
  parent_id: number | null;
  n_level: number | null;
}

export interface FetchUnidadesResult {
  generators: UnidadesGenerator[];
  establishments: UnidadesEstablishment[];
}

/** `GET /generators/me` como a API devolve: ids numéricos, `name` pode vir nulo. */
export interface GeneratorsMeResponse {
  generators: Array<{ id: number; name: string | null; external_id: string }>;
  establishments: Array<{
    id: number;
    name: string | null;
    external_id: string;
    parent_id: number | null;
    generator_id: number | null;
    n_level: number | null;
    state?: string | null;
    city?: string | null;
  }>;
  states: Array<{ name: string; value: string }>;
}

export class GeneratorApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'GeneratorApiError';
  }

  /** A API responde 403 tanto para token ausente/inválido quanto para realm não permitido. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/** DRF: `{ detail }` para erro simples, `{ campo: [mensagens] }` para validação. */
type ApiErrorBody = { detail?: string } & Record<string, unknown>;

export function messageFromBody(body: ApiErrorBody | null, status: number): string {
  if (typeof body?.detail === 'string' && body.detail) return body.detail;

  const fieldErrors = Object.values(body ?? {})
    .flatMap((value) => (Array.isArray(value) ? value : []))
    .filter((value): value is string => typeof value === 'string');
  if (fieldErrors.length) return fieldErrors.join(' ');

  return `Erro ${status} na API de geradores`;
}

async function request<T>(path: string): Promise<T> {
  const token = await getValidToken();
  const res = await fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
  });

  if (!res.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      body = null;
    }
    throw new GeneratorApiError(res.status, messageFromBody(body, res.status));
  }

  return (await res.json()) as T;
}

export async function fetchGeneratorsMe(): Promise<GeneratorsMeResponse> {
  return request('/generators/me');
}

/**
 * Geradores, unidades e UFs do escopo do usuário — os filtros globais do portal.
 *
 * Ids viram texto: o store e os filtros comparam `id` com `external_id` como string, herança do
 * tipo `ID` do GraphQL que o antigo gateway devolvia.
 */
export async function fetchGeneratorsAndEstablishments(): Promise<FetchFilterOptionsResult> {
  const me = await fetchGeneratorsMe();
  return {
    generators: (me.generators ?? []).map((g) => ({
      id: String(g.id),
      name: g.name ?? '',
      external_id: g.external_id,
    })),
    establishments: (me.establishments ?? []).map((e) => ({
      id: String(e.id),
      name: e.name ?? '',
      external_id: e.external_id,
      parent_id: e.parent_id,
      n_level: e.n_level ?? undefined,
      generator_id: e.generator_id != null ? String(e.generator_id) : null,
      state: e.state ?? null,
      city: e.city ?? null,
    })),
    states: me.states ?? [],
  };
}

/** Geradores e unidades para a gestão de usuário — mesma consulta, recorte menor. */
export async function fetchUnidades(): Promise<FetchUnidadesResult> {
  const me = await fetchGeneratorsMe();
  return {
    generators: (me.generators ?? []).map((g) => ({ id: String(g.id), name: g.name ?? '' })),
    establishments: (me.establishments ?? []).map((e) => ({
      id: String(e.id),
      name: e.name ?? '',
      generator_id: e.generator_id != null ? String(e.generator_id) : null,
      parent_id: e.parent_id,
      n_level: e.n_level,
    })),
  };
}
